import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence, connectAuthEmulator, Auth } from 'firebase/auth';
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
  terminate, clearIndexedDbPersistence, connectFirestoreEmulator,
} from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// Dentro de la app de iOS, getAuth() intenta cargar las ventanas emergentes de
// la web (iframes de gapi) y puede quedarse colgado. Allí se inicia a mano,
// sin ventanas: Apple y Google entran por el login nativo (accesoNativo.ts).
export const auth: Auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app);
// Los correos de Firebase (confirmar el email, cambiar la contraseña) en español
auth.languageCode = 'es';

// Caché en el dispositivo: la app abre al instante con lo último que vio y se
// puede consultar sin conexión (en el metro, en un avión, de viaje con el plan
// de A medias). Lo que se apunte sin red se sube solo al volver la conexión.
// Vale para varias pestañas abiertas a la vez en la web.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

// Solo para probar en local contra los emuladores de Firebase (auth en 9099 y
// firestore en 8391, ver firebase.json): REACT_APP_EMULADORES=1 npm start
if (process.env.REACT_APP_EMULADORES === '1') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8391);
}

/**
 * Borra la caché del dispositivo. Se llama al cerrar sesión: en un ordenador
 * compartido, los datos de quien sale no deben quedarse guardados. Firestore
 * queda inservible después, así que hay que recargar la página.
 */
export async function borrarCacheLocal(): Promise<void> {
  try {
    await terminate(db);
    await clearIndexedDbPersistence(db);
  } catch (e) {
    console.warn('No se pudo borrar la caché local', e);
  }
}
